import { MapPin, Phone, Mail, Clock } from "lucide-react";

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer
      id="contact"
      data-testid="footer-section"
      className="bg-primary text-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_shield-agency/artifacts/ojmr7nlc_Logo_PSI_RGB.png" 
              alt="Palmetto South Insurance" 
              className="h-14 w-auto mb-4 brightness-0 invert"
            />
            <p className="text-white/80 mb-6 max-w-md">
              Your trusted local insurance partner. We're committed to protecting
              what matters most to you with personalized coverage and exceptional
              service.
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/palmettosouthins"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-facebook"
                className="h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                aria-label="Facebook"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>
              <a
                href="https://www.linkedin.com/company/palmetto-south-insurance-group-llc"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-linkedin"
                className="h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                aria-label="LinkedIn"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-4">Contact Us</h4>
            <ul className="space-y-4">
              <li>
                <a
                  href="tel:+18642089181"
                  data-testid="footer-phone"
                  className="flex items-center gap-3 text-white/80 hover:text-white transition-colors"
                >
                  <Phone className="h-5 w-5 text-accent flex-shrink-0" />
                  <span className="text-sm">(864) 208-9181</span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:daniel.bass@palmettosouthins.com"
                  data-testid="footer-email"
                  className="flex items-center gap-3 text-white/80 hover:text-white transition-colors"
                >
                  <Mail className="h-5 w-5 text-accent flex-shrink-0" />
                  <span className="text-sm">daniel.bass@palmettosouthins.com</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Hours */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-4">Office Hours</h4>
            <p className="text-sm text-accent mb-3 italic">By appointment only</p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
                <div className="text-sm text-white/80">
                  <div className="mb-1">Monday - Thursday</div>
                  <div className="text-white">9:00 AM - 5:00 PM</div>
                </div>
              </li>
              <li className="ml-8 text-sm text-white/80">
                <div className="mb-1">Friday</div>
                <div className="text-white">9:00 AM - 12:00 PM</div>
              </li>
              <li className="ml-8 text-sm text-white/80">
                <div className="mb-1">Saturday & Sunday</div>
                <div className="text-white">Closed</div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/60">
              © {currentYear} Palmetto South Insurance Group LLC. All rights reserved.
            </p>
            <div className="flex gap-6">
              <a
                href="#"
                className="text-sm text-white/60 hover:text-white transition-colors"
              >
                Privacy Policy
              </a>
              <a
                href="#"
                className="text-sm text-white/60 hover:text-white transition-colors"
              >
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
